import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Account } from '../types';
import { addFavorite, createBooking, getAccount, removeFavorite } from '../lib/api';
import { ApiError } from '../lib/api';
import ScreenshotGallery from '../components/ScreenshotGallery';
import DragScrollRow from '../components/DragScrollRow';
import PriceTag from '../components/PriceTag';
import RankBadge from '../components/RankBadge';
import CollectorTierBadge from '../components/CollectorTierBadge';
import TierCounter from '../components/TierCounter';
import SkinIcon from '../components/SkinIcon';
import ConfirmModal from '../components/ConfirmModal';
import EmptyState from '../components/EmptyState';
import MainButtonFallback from '../components/MainButtonFallback';
import { formatNumber } from '../lib/format';
import { useCatalogStore } from '../store/catalogStore';
import { useToastStore } from '../store/toastStore';
import {
  hapticImpact,
  hapticNotification,
  hideBackButton,
  hideMainButton,
  setBackButton,
  setMainButton,
} from '../lib/telegram';

const STATUS_LABEL: Record<string, string> = {
  RESERVED: 'Забронирован',
  SOLD: 'Продан',
  PENDING: 'На модерации',
  REJECTED: 'Отклонён',
};

export default function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { meta } = useCatalogStore();
  const showToast = useToastStore((s) => s.show);

  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorite, setFavorite] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getAccount(id)
      .then(setAccount)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить аккаунт'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setBackButton(() => navigate(-1));
    return () => hideBackButton();
  }, [navigate]);

  // useState-based `booking` isn't a synchronous guard — see the identical comment in
  // SellWizardPage.submit() for why a ref is needed to survive rapid repeat taps.
  const bookingRef = useRef(false);

  const doBook = async () => {
    if (!account || bookingRef.current) return;
    bookingRef.current = true;
    setBooking(true);
    try {
      await createBooking(account.id, note.trim() || undefined);
      hapticNotification('success');
      setBooked(true);
      setConfirmOpen(false);
    } catch (e) {
      hapticNotification('error');
      showToast(e instanceof ApiError ? e.message : 'Не удалось забронировать', 'error');
    } finally {
      bookingRef.current = false;
      setBooking(false);
    }
  };

  useEffect(() => {
    if (!account || booked || account.status !== 'ACTIVE') {
      hideMainButton();
      return;
    }
    setMainButton({
      text: `Забронировать за ${account.price} ${account.currency}`,
      onClick: () => {
        hapticImpact('medium');
        setConfirmOpen(true);
      },
    });
    return () => hideMainButton();
  }, [account, booked]);

  const rankLabel = useMemo(
    () => meta?.ranks.find((r) => r.id === account?.rank)?.name ?? account?.rank ?? '',
    [meta, account]
  );
  const skinsByCollab = useMemo(() => {
    if (!account) return [];
    const groups = new Map<string, { name: string; skins: Account['skins'] }>();
    for (const s of account.skins) {
      const groupKey = s.collab ?? s.series ?? s.tier;
      const groupName = s.collabName ?? s.seriesName ?? (s.tier === 'legend' ? 'Legend' : 'Collector');
      if (!groups.has(groupKey)) groups.set(groupKey, { name: groupName, skins: [] });
      groups.get(groupKey)!.skins.push(s);
    }
    return [...groups.entries()].map(([key, v]) => ({ key, ...v }));
  }, [account]);

  if (loading) {
    return <div className="p-4 text-center text-white/40">Загрузка...</div>;
  }
  if (error || !account) {
    return <EmptyState icon="😕" title="Аккаунт не найден" subtitle={error ?? undefined} />;
  }

  if (booked) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="text-5xl">✅</div>
        <div className="text-lg font-semibold text-white">Заявка отправлена!</div>
        <div className="text-sm text-white/60">
          Администратор ARC TEAM свяжется с вами в течение 24 часов для проведения сделки.
        </div>
        <button
          onClick={() => navigate('/')}
          className="mt-4 rounded-full bg-mlbb-accent px-5 py-2.5 text-sm font-medium text-white"
        >
          Вернуться в каталог
        </button>
      </div>
    );
  }

  const statusLabel = STATUS_LABEL[account.status];

  return (
    <div className="pb-28">
      <div className="relative">
        <ScreenshotGallery urls={account.screenshots.map((s) => s.url)} />
        <button
          onClick={async () => {
            hapticImpact('light');
            setFavorite((f) => !f);
            try {
              if (favorite) await removeFavorite(account.id);
              else await addFavorite(account.id);
            } catch {
              /* ignore */
            }
          }}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-lg backdrop-blur-sm"
        >
          {favorite ? '❤️' : '🤍'}
        </button>
        {statusLabel && (
          <div className="absolute left-3 top-3 rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white">
            {statusLabel}
          </div>
        )}
      </div>

      <div className="space-y-5 p-4">
        <div className="flex items-center justify-between">
          <PriceTag price={account.price} currency={account.currency} size="lg" />
          <RankBadge rankId={account.rank} label={rankLabel} />
        </div>

        {account.gameId && (
          <div className="grid grid-cols-2 gap-2 rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-sm">
            <div className="col-span-2 text-xs text-white/40">🔒 Видно только вам (продавцу) и админам</div>
            <div className="text-white/50">Game ID</div>
            <div className="text-right text-white">{account.gameId}</div>
            <div className="text-white/50">Server ID</div>
            <div className="text-right text-white">{account.serverId}</div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-sm">
          <div className="text-white/50">Сервер</div>
          <div className="text-right text-white">{account.server}</div>
          {account.heroesCount && (
            <>
              <div className="text-white/50">Героев</div>
              <div className="text-right text-white">{account.heroesCount}</div>
            </>
          )}
        </div>

        {account.collectorTier && (
          <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-3">
            <div className="mb-2 text-sm text-white/70">Коллекция</div>
            <CollectorTierBadge tierId={account.collectorTier} level={account.collectorTierLevel} />
          </div>
        )}

        <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-3">
          <div className="mb-2 text-sm text-white/70">Скины ({account.totalSkins} всего)</div>
          <TierCounter legend={account.legendCount} grand={account.grandCount} collector={account.collectorCount} />
        </div>

        {skinsByCollab.length > 0 && (
          <div className="space-y-4">
            <div className="text-sm font-semibold text-white">Ключевые скины</div>
            {skinsByCollab.map((group) => (
              <div key={group.key}>
                <div className="mb-1.5 text-xs text-white/50">
                  {group.name} ({group.skins.length})
                </div>
                <DragScrollRow className="flex gap-2 pb-1">
                  {group.skins.map((s) => (
                    <div key={s.key} className="flex flex-col items-center gap-1">
                      <SkinIcon icon={s.icon} hero={s.hero} tier={s.tier} size={56} />
                      <div className="w-14 truncate text-center text-[10px] text-white/50">{s.hero}</div>
                    </div>
                  ))}
                </DragScrollRow>
              </div>
            ))}
          </div>
        )}

        {(account.recallStunning > 0 ||
          account.recallLuxurious > 0 ||
          account.towerSkins > 0 ||
          account.loadingStunning > 0 ||
          account.loadingLuxurious > 0 ||
          account.loadingStellar > 0) && (
          <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-sm">
            <div className="mb-2 text-white/70">Эффекты</div>
            <div className="grid grid-cols-2 gap-1.5 text-white/80">
              {account.recallStunning > 0 && <div>🌀 Recall Stunning: {account.recallStunning}</div>}
              {account.recallLuxurious > 0 && <div>🌀 Recall Luxurious: {account.recallLuxurious}</div>}
              {account.towerSkins > 0 && <div>🗼 Tower: {account.towerSkins}</div>}
              {account.loadingStunning > 0 && <div>🖼️ Loading Stunning: {account.loadingStunning}</div>}
              {account.loadingLuxurious > 0 && <div>🖼️ Loading Luxurious: {account.loadingLuxurious}</div>}
              {account.loadingStellar > 0 && <div>🖼️ Loading Stellar: {account.loadingStellar}</div>}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-sm">
          <div className="mb-2 text-white/70">Валюты</div>
          <div className="text-white/80">💎 Алмазы: {formatNumber(account.diamonds)}</div>
          <div className="mt-1.5">
            <div className="mb-1 text-xs text-white/50">🎡 Magic Wheel {account.magicWheelProgress}/200</div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-mlbb-border">
              <div
                className="h-full bg-mlbb-accent"
                style={{ width: `${Math.min(100, (account.magicWheelProgress / 200) * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {account.description && (
          <div>
            <div className="mb-1 text-sm font-semibold text-white">Описание продавца</div>
            <p className="whitespace-pre-wrap text-sm text-white/70">{account.description}</p>
          </div>
        )}
      </div>

      {account.status === 'ACTIVE' && !booked && (
        <MainButtonFallback
          text={`Забронировать за ${account.price} ${account.currency}`}
          onClick={() => {
            hapticImpact('medium');
            setConfirmOpen(true);
          }}
        />
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Забронировать аккаунт?"
        message="После брони с вами свяжется администратор ARC TEAM для проведения сделки. Ваш Telegram будет передан продавцу."
        confirmLabel={booking ? 'Отправка...' : 'Подтвердить'}
        confirmDisabled={booking}
        onConfirm={doBook}
        onCancel={() => setConfirmOpen(false)}
      >
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Комментарий продавцу (необязательно)"
          rows={2}
          className="w-full resize-none rounded-lg border border-mlbb-border bg-mlbb-bg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-mlbb-accent-from"
        />
      </ConfirmModal>
    </div>
  );
}
